import Beneficiaries from 'Beneficiaries/components/Beneficiaries';
import BeneficiaryForm from 'Beneficiaries/components/BeneficiaryForm';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import * as E from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import qs from 'query-string';
import React from 'react';
import {useSelector} from 'react-redux';
import {Redirect, Route, Switch, useHistory, useLocation, useParams} from 'react-router';
import Loading from 'Shared/components/Loading';
import {Scope} from 'Shared/constants/Scope';
import {selectUserScopesEither} from 'Users/selectors/userProfileSelectors';
import BeneficiaryDetail from './components/BeneficiaryDetail';
import WalmartBeneficiaryDetail from './components/WalmartBeneficiaryDetail';

const ROOT_URI = '/beneficiaries';
export const BeneficiariesRouteConstants = {
  BENEFICIARIES: ROOT_URI,
  BENEFICIARY_DETAIL: (policyNumber: string) => `${ROOT_URI}/${policyNumber}`,
  BENEFICIARY_UPDATE: (id: string, policyNumber: string) => `${ROOT_URI}/${policyNumber}/update?id=${id}`,
  BENEFICIARY_PERSON_ADD: (policyNumber: string) => `${ROOT_URI}/${policyNumber}/add`,
};

const UpdateBeneficiaryPersonRoute = () => {
  const {search} = useLocation();
  const {policyNumber} = useParams();
  const currentScopesEither = useSelector(selectUserScopesEither);

  const id = qs.parse(search).id as string;

  useBreadcrumbs('Update', BeneficiariesRouteConstants.BENEFICIARY_UPDATE(id, policyNumber!));

  const renderBeneficiaryUpdate = (currentScopes: Scope[]) => {
    const canUpdateBeneficiary = currentScopes.includes(Scope.BeneficiariesEdit);

    return canUpdateBeneficiary ? <Route render={() => <BeneficiaryForm />} /> : <Redirect to={'/unauthorized'} />;
  };

  return (
    <Route
      render={() =>
        pipe(
          currentScopesEither,
          E.fold(() => <Loading />, renderBeneficiaryUpdate)
        )
      }
    />
  );
};

const AddBeneficiaryPersonRoute = () => {
  const {policyNumber} = useParams();
  const currentScopesEither = useSelector(selectUserScopesEither);
  const history = useHistory();

  if (!policyNumber) {
    history.push(BeneficiariesRouteConstants.BENEFICIARIES);
  }

  useBreadcrumbs('Add', BeneficiariesRouteConstants.BENEFICIARY_PERSON_ADD(policyNumber!));

  const renderBeneficiaryPersonAdd = (currentScopes: Scope[]) => {
    const canAddBeneficiary = currentScopes.includes(Scope.BeneficiariesAdd);

    return canAddBeneficiary ? <Route render={() => <BeneficiaryForm />} /> : <Redirect to={'/unauthorized'} />;
  };

  return (
    <Route
      render={() =>
        pipe(
          currentScopesEither,
          E.fold(() => <Loading />, renderBeneficiaryPersonAdd)
        )
      }
    />
  );
};

const WalmartCompanyCode = '53';

const BeneficiaryPolicyRoutes = () => {
  const {policyNumber} = useParams();
  // All Walmart Policy will start with Walmart Company Code => 53
  const isWalmartPolicy = policyNumber?.startsWith(WalmartCompanyCode);
  const history = useHistory();

  if (!policyNumber) {
    history.push(BeneficiariesRouteConstants.BENEFICIARIES);
  }

  useBreadcrumbs(policyNumber!, BeneficiariesRouteConstants.BENEFICIARY_DETAIL(policyNumber!));

  return (
    <Switch>
      <Route exact path={`${ROOT_URI}/:policyNumber/update`} render={() => <UpdateBeneficiaryPersonRoute />} />
      <Route exact path={`${ROOT_URI}/:policyNumber/add`} render={() => <AddBeneficiaryPersonRoute />} />
      <Route
        exact
        path={`${ROOT_URI}/:policyNumber`}
        render={() => (isWalmartPolicy ? <WalmartBeneficiaryDetail /> : <BeneficiaryDetail />)}
      />
    </Switch>
  );
};

const BeneficiariesRoute = () => {
  const currentScopesEither = useSelector(selectUserScopesEither);
  useBreadcrumbs('Beneficiaries', BeneficiariesRouteConstants.BENEFICIARIES);

  const renderBeneficiaries = (currentScopes: Scope[]) => {
    const canViewBeneficiaryDetails = currentScopes.includes(Scope.BeneficiariesSearch);

    return canViewBeneficiaryDetails ? (
      <Route
        render={({match}) => (
          <Switch>
            <Route exact path={match.url} render={() => <Beneficiaries />} />
            {/* This can not be exact match, important */}
            <Route path={`${match.url}/:policyNumber`} render={() => <BeneficiaryPolicyRoutes />} />
          </Switch>
        )}
      />
    ) : (
      <Redirect to={'/unauthorized'} />
    );
  };

  return (
    <Route
      render={() =>
        pipe(
          currentScopesEither,
          E.fold(() => <Loading />, renderBeneficiaries)
        )
      }
    />
  );
};

export default () => <Route path={ROOT_URI} render={() => <BeneficiariesRoute />} />;
